import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import {
  ChevronLeft, MapPin, Clock, CheckCircle2, ShieldCheck, Store, Phone, Globe,
  Instagram, Music2, Star, Package, Users, Utensils, QrCode, Share2, Sparkles,
  Loader2, ExternalLink, AlertTriangle, MessageCircle, Pencil,
} from "lucide-react";
import SmartImage from "../components/SmartImage";
import StorefrontLocationPicker from "../components/StorefrontLocationPicker";
import CategoryIcon from "../components/CategoryIcon";
import { formatCategoryLabel } from "../lib/categories";
import { getActiveSale, effectivePrice } from "../lib/promo";
import { SalePill } from "../components/SalePill";
import UserGem from "../components/UserGem";
import AvatarFrame from "../components/AvatarFrame";
import VerifiedBadge from "../components/VerifiedBadge";
import GiftButton from "../components/GiftButton";
import TipDialog from "../components/TipDialog";
import { Button } from "../components/ui/button";

const DAYS = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

const CRED_LABEL = {
  food_handler: "Food Handler",
  food_manager: "Food Manager",
  business_license: "Business License",
  sellers_permit: "Seller's Permit",
  health_permit: "Health Permit",
  cottage_food: "Cottage Food",
  professional_cert: "Professional Cert.",
  other: "Other",
  custom: "Custom",
};

const OPEN_STATE_META = {
  open:            { label: "Open now", tone: "emerald" },
  special_open:    { label: "Open (special hours)", tone: "emerald" },
  outside_hours:   { label: "Closed — outside hours", tone: "zinc" },
  closed_hours:    { label: "Closed today", tone: "zinc" },
  special_closed:  { label: "Closed today (holiday)", tone: "zinc" },
  paused:          { label: "Paused by owner", tone: "amber" },
  vacation:        { label: "On vacation", tone: "amber" },
  suspended:       { label: "Suspended", tone: "rose" },
  closed:          { label: "Closed permanently", tone: "rose" },
  archived:        { label: "Archived", tone: "rose" },
  draft:           { label: "Draft", tone: "zinc" },
};

function OpenStatePill({ state }) {
  const key = state?.is_open ? state?.reason : (state?.reason || state?.status || "outside_hours");
  const meta = OPEN_STATE_META[key] || { label: state?.is_open ? "Open" : "Closed", tone: state?.is_open ? "emerald" : "zinc" };
  const dot = state?.is_open ? "bg-emerald-400" : "bg-zinc-500";
  const cls = state?.is_open
    ? "bg-emerald-500/15 border-emerald-400/40 text-emerald-100"
    : meta.tone === "amber"
      ? "bg-amber-500/15 border-amber-400/40 text-amber-100"
      : meta.tone === "rose"
        ? "bg-rose-500/15 border-rose-400/40 text-rose-100"
        : "bg-white/5 border-white/15 text-zinc-200";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black border ${cls}`} data-testid="storefront-open-state">
      <span className={`w-2 h-2 rounded-full ${dot} ${state?.is_open ? "animate-pulse" : ""}`} />
      {meta.label}
    </span>
  );
}

function HoursTable({ hours = {}, special_hours = [] }) {
  const today = new Date();
  const todayIdx = (today.getDay() + 6) % 7; // Mon=0
  const specialByDate = useMemo(() => {
    const m = new Map();
    (special_hours || []).forEach((sh) => { if (sh?.date) m.set(sh.date, sh); });
    return m;
  }, [special_hours]);
  const iso = today.toISOString().slice(0, 10);
  const todaySpecial = specialByDate.get(iso);
  return (
    <div className="space-y-1.5" data-testid="storefront-hours">
      {DAYS.map((d, i) => {
        const slots = hours?.[d.key] || [];
        const isToday = i === todayIdx;
        const closed = !slots || slots.length === 0;
        return (
          <div
            key={d.key}
            className={`flex items-center justify-between text-sm ${isToday ? "font-black text-white" : "text-zinc-300"}`}
            data-testid={`storefront-hours-row-${d.key}`}
          >
            <span className="w-14">{d.label}{isToday && <span className="text-pink-300 text-[10px] ml-1 uppercase tracking-widest">Today</span>}</span>
            <span className="text-right">
              {closed ? <span className="text-zinc-500">Closed</span> :
                slots.map((s, si) => (
                  <span key={si} className="ml-2">{s.open}–{s.close}</span>
                ))}
            </span>
          </div>
        );
      })}
      {todaySpecial && (
        <div className="mt-2 px-3 py-2 rounded-xl bg-amber-500/10 border border-amber-400/30 text-xs text-amber-100" data-testid="storefront-special-hours">
          <span className="uppercase tracking-widest font-black mr-1.5">Today only:</span>
          {todaySpecial.closed ? "Closed" : `${todaySpecial.open}–${todaySpecial.close}`}
        </div>
      )}
      {(special_hours || []).filter((sh) => sh?.date && sh.date > iso).slice(0, 3).map((sh) => (
        <div key={sh.date} className="text-[11px] text-zinc-400 flex items-center justify-between">
          <span>{new Date(sh.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
          <span>{sh.closed ? "Closed" : `${sh.open}–${sh.close}`}</span>
        </div>
      ))}
    </div>
  );
}

export default function PublicStorefront() {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addItem, cart } = useCart();
  const [locations, setLocations] = useState([]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [shopData, setShopData] = useState(null); // { shop, primary_location, reputation, open_state, public_credentials, verification_achievements }
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [team, setTeam] = useState([]);
  const [reviews, setReviews] = useState({ reviews: [], count: 0, average: 0 });
  const [achievements, setAchievements] = useState([]);
  const [showQr, setShowQr] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const { data } = await api.get(`/shops/${shopId}`);
      setShopData(data);
      const businessId = data?.shop?.business_id;
      const ownerUserId = data?.shop?.user_id;
      const [prodRes, teamRes, achRes, revRes, dealsRes] = await Promise.all([
        ownerUserId ? api.get("/products", { params: { vendor_id: ownerUserId, limit: 60 } }).catch(() => ({ data: [] })) : { data: [] },
        businessId ? api.get(`/businesses/${businessId}/team`).catch(() => ({ data: { members: [] } })) : { data: { members: [] } },
        businessId ? api.get(`/businesses/${businessId}/achievements`).catch(() => ({ data: { items: [] } })) : { data: { items: [] } },
        ownerUserId ? api.get(`/vendors/${ownerUserId}/reviews`).catch(() => ({ data: { reviews: [], count: 0, average: 0 } })) : { data: { reviews: [], count: 0, average: 0 } },
        api.get(`/shops/${shopId}/deals`).catch(() => ({ data: { items: [] } })),
      ]);
      api.get(`/shops/${shopId}/locations`).then((r) => setLocations(r.data.locations || [])).catch(() => setLocations([]));
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
      setTeam(teamRes.data.members || []);
      setAchievements(achRes.data.items || []);
      setReviews(revRes.data || { reviews: [], count: 0, average: 0 });
      setDeals(dealsRes.data.items || []);
      // Fire-and-forget analytics
      api.post(`/shops/${shopId}/track-view`).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else toast.error(err.response?.data?.detail || "Couldn't load shop");
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const share = async () => {
    const url = `${window.location.origin}/shop/${shopId}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: shopData?.shop?.name || "Hidden Gem shop", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch {}
  };

  const onAdd = async (p) => {
    const vendorId = shopData?.shop?.user_id;
    if (user && locations.length > 1 && !cart?.locations?.[vendorId]) {
      toast.error("Pick a location first");
      document.getElementById("storefront-location-picker")?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    try {
      const res = await addItem(p.product_id, 1);
      if (res?.needs_customization) {
        toast.message(`Customize ${p.name}`);
      } else {
        toast.success(`${p.name} added`);
      }
    } catch {
      toast.error("Couldn't add to bag");
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] grid place-items-center" data-testid="storefront-loading">
        <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
      </div>
    );
  }
  if (notFound || !shopData?.shop) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-3" data-testid="storefront-not-found">
        <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto" />
        <h1 className="text-3xl font-black tracking-tighter">Shop not found</h1>
        <p className="text-sm text-zinc-400">This storefront may have been closed or the link is broken.</p>
        <Button onClick={() => navigate("/feed")} className="rounded-full bg-pink-500 hover:bg-pink-400 text-white h-11 px-5" data-testid="storefront-back-to-feed">Back to feed</Button>
      </div>
    );
  }

  const { shop, primary_location: loc, reputation, open_state, public_credentials = [], verification_achievements = [] } = shopData;
  const isOwner = user && shop.user_id === user.user_id;
  const shareUrl = `${window.location.origin}/shop/${shopId}`;
  const backendBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="storefront-page">
      {/* Banner */}
      <div className="relative">
        <div className="aspect-[3/1] bg-gradient-to-br from-pink-500/20 to-cyan-500/20 border-b border-white/10 overflow-hidden" data-testid="storefront-banner">
          {shop.banner_url ? (
            <SmartImage src={fileUrl(shop.banner_url)} alt={shop.name} mode="smart_auto_fit" className="w-full h-full" testid="storefront-banner-image" />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <Store className="w-14 h-14 text-white/20" />
            </div>
          )}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="absolute top-3 left-3 h-9 px-3 rounded-full bg-black/60 hover:bg-black/80 text-white inline-flex items-center gap-1 text-xs font-bold"
          data-testid="storefront-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="absolute top-3 right-3 flex items-center gap-2">
          <button
            onClick={share}
            className="h-9 px-3 rounded-full bg-black/60 hover:bg-black/80 text-white inline-flex items-center gap-1 text-xs font-bold"
            data-testid="storefront-share"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button
            onClick={() => setShowQr(true)}
            className="h-9 px-3 rounded-full bg-black/60 hover:bg-black/80 text-white inline-flex items-center gap-1 text-xs font-bold"
            data-testid="storefront-open-qr"
          >
            <QrCode className="w-4 h-4" /> QR
          </button>
          {isOwner && (
            <button
              onClick={() => navigate("/sell/shop")}
              className="h-9 px-3 rounded-full bg-pink-500 hover:bg-pink-400 text-white inline-flex items-center gap-1 text-xs font-black glow-pink"
              data-testid="storefront-edit-shop"
            >
              <Pencil className="w-4 h-4" /> Edit
            </button>
          )}
        </div>
      </div>

      {/* Identity block */}
      <section className="px-4 sm:px-6 -mt-10 relative z-10" data-testid="storefront-identity">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          <AvatarFrame userId={shop.user_id} size={88} inset={4} noFrameClass="bg-zinc-900 border-2 border-white/20 rounded-full" testid="storefront-avatar-frame">
            {shop.logo_url ? (
              <img src={fileUrl(shop.logo_url)} alt={shop.name} className="w-full h-full object-cover" data-testid="storefront-logo" />
            ) : (
              <div className="w-full h-full grid place-items-center bg-pink-500/20"><Store className="w-9 h-9 text-pink-200" /></div>
            )}
          </AvatarFrame>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-3xl sm:text-4xl font-black tracking-tighter truncate" data-testid="storefront-name">{shop.name}</h1>
              {reputation?.tier && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] font-black uppercase tracking-widest" data-testid="storefront-reputation">
                  <Sparkles className="w-3 h-3 text-pink-300" /> {reputation.tier_label || reputation.tier_name}
                </span>
              )}
              <OpenStatePill state={open_state} />
            </div>
            {shop.tagline && <p className="text-sm text-pink-200 font-bold mt-1" data-testid="storefront-tagline">{shop.tagline}</p>}
            <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs text-zinc-300">
              {shop.vendor_type && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10 capitalize">
                  <Utensils className="w-3 h-3" /> {shop.vendor_type}
                </span>
              )}
              {loc?.address && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 border border-white/10" data-testid="storefront-location-chip">
                  <MapPin className="w-3 h-3" /> {loc.address}
                </span>
              )}
            </div>
          </div>
        </div>

        {shop.vacation_message && open_state?.status === "vacation" && (
          <div className="mt-4 px-4 py-3 rounded-2xl border border-amber-400/40 bg-amber-500/10 text-amber-100 text-sm" data-testid="storefront-vacation">
            <p className="font-black tracking-tight uppercase text-[10px] mb-1">Vacation notice</p>
            <p>{shop.vacation_message}</p>
          </div>
        )}
        {shop.announcement && open_state?.status !== "vacation" && (
          <div className="mt-4 px-4 py-3 rounded-2xl border border-cyan-400/40 bg-cyan-500/10 text-cyan-50 text-sm" data-testid="storefront-announcement">
            <p className="font-black tracking-tight uppercase text-[10px] mb-1">Announcement</p>
            <p>{shop.announcement}</p>
          </div>
        )}

        {/* Action row */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="storefront-actions">
          {user && shop.user_id !== user.user_id && (
            <>
              <TipDialog vendor={{ user_id: shop.user_id, name: shop.name, vendor_profile: { avatar_url: shop.logo_url } }} source="storefront" />
              <GiftButton
                recipient={{ user_id: shop.user_id, name: shop.name, picture: shop.logo_url }}
                targetKind="vendor"
                targetId={shop.user_id}
                variant="pill"
                testIdPrefix="storefront-gift"
              />
              <button
                type="button"
                onClick={() => navigate(`/messages/${shop.user_id}`)}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold text-sm"
                data-testid="storefront-message"
              >
                <MessageCircle className="w-4 h-4" /> Message
              </button>
            </>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 sm:px-6 mt-6">
        {/* Left: menu/products (2 cols on lg) */}
        <div className="lg:col-span-2 space-y-4">
          {/* Deals */}
          {deals.length > 0 && (
            <section className="rounded-3xl bg-gradient-to-br from-amber-500/10 to-pink-500/10 border border-amber-400/30 p-5" data-testid="storefront-deals">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2 text-amber-100">
                <Sparkles className="w-4 h-4" /> Deals & offers ({deals.length})
              </h2>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {deals.map((d) => (
                  <div key={d.promotion_id} className="rounded-2xl border border-amber-400/40 bg-amber-500/5 p-3" data-testid={`storefront-deal-${d.promotion_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-black text-amber-100 truncate">{d.name}</p>
                      {d.status === "scheduled" && <span className="text-[9px] uppercase tracking-widest font-black text-cyan-200">Starts soon</span>}
                    </div>
                    <p className="text-[11px] text-amber-200/90 mt-0.5">
                      {d.type === "percent_off" && `${d.value}% off`}
                      {d.type === "flat_off" && `$${(d.value / 100).toFixed(2)} off`}
                      {d.type === "bogo" && `Buy ${d.value} get 1`}
                      {d.type === "free_shipping" && "Free shipping"}
                      {d.min_subtotal_cents ? ` · min $${(d.min_subtotal_cents / 100).toFixed(2)}` : ""}
                      {d.expires_at ? ` · ends ${new Date(d.expires_at).toLocaleDateString()}` : ""}
                    </p>
                    {d.code && d.code_public && (
                      <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 border border-amber-400/50 text-amber-100 text-xs font-black tracking-widest" data-testid={`storefront-deal-code-${d.promotion_id}`}>
                        Code: {d.code}
                      </div>
                    )}
                    {d.description && <p className="text-[11px] text-zinc-300 mt-1">{d.description}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* About */}
          {shop.bio && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-about">
              <h2 className="text-lg font-black tracking-tight">About</h2>
              <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{shop.bio}</p>
            </section>
          )}

          {/* Cuisine tags */}
          {(shop.cuisine_tags || []).length > 0 && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-cuisine-tags">
              <h2 className="text-lg font-black tracking-tight">What we make</h2>
              <div className="flex flex-wrap gap-2 mt-3">
                {(shop.cuisine_tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-white/5 text-zinc-200 border border-white/10" data-testid={`storefront-tag-${t}`}>
                    <CategoryIcon slug={t} size={13} strokeWidth={2.2} />
                    {formatCategoryLabel(t)}
                  </span>
                ))}
              </div>
            </section>
          )}

          <StorefrontLocationPicker shopId={shopId} vendorId={shop.user_id} locations={locations} />

          {/* Menu */}
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-menu">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
                <Package className="w-4 h-4" /> Menu ({products.length})
              </h2>
            </div>
            {products.length === 0 ? (
              <p className="text-center text-zinc-500 py-8 text-sm">No listings yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {products.map((p) => (
                  <div key={p.product_id} className="rounded-2xl overflow-hidden bg-zinc-950 border border-white/5" data-testid={`storefront-product-${p.product_id}`}>
                    <div className="aspect-square bg-zinc-900 relative">
                      {p.image_paths?.[0] && (
                        <SmartImage src={fileUrl(p.image_paths[0])} alt={p.name} mode="smart_auto_fit" className={`w-full h-full ${p.is_sold_out ? "opacity-50" : ""}`} testid={`storefront-product-img-${p.product_id}`} />
                      )}
                      <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
                        {p.is_sold_out && (<span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-500 text-white font-black">Sold out</span>)}
                        {!p.is_sold_out && p.is_limited && (<span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400 text-black font-black">Limited</span>)}
                        <SalePill product={p} />
                      </div>
                    </div>
                    <div className="p-3">
                      <p className="font-bold truncate text-sm">{p.name}</p>
                      <div className="flex items-center justify-between mt-1">
                        {(() => {
                          const sale = getActiveSale(p);
                          return (
                            <span className="inline-flex items-baseline gap-1.5">
                              <span className="text-pink-400 font-black">${effectivePrice(p).toFixed(2)}</span>
                              {sale && (<span className="text-xs text-zinc-500 line-through">${Number(p.price).toFixed(2)}</span>)}
                            </span>
                          );
                        })()}
                        <button
                          onClick={() => onAdd(p)}
                          disabled={p.is_sold_out}
                          className="h-8 px-3 rounded-full bg-pink-500 hover:bg-pink-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-xs font-bold text-white glow-pink disabled:glow-none"
                          data-testid={`storefront-add-${p.product_id}`}
                        >
                          {p.is_sold_out ? "Out" : "Add"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Reviews */}
          {reviews.count > 0 && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-reviews">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
                <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {reviews.average} avg ({reviews.count} reviews)
              </h2>
              <div className="mt-3 space-y-2">
                {reviews.reviews.slice(0, 5).map((r) => (
                  <div key={r.review_id} className="rounded-2xl bg-white/[0.02] border border-white/10 p-3" data-testid={`storefront-review-${r.review_id}`}>
                    <div className="flex items-center gap-1 text-amber-400">
                      {[1,2,3,4,5].map((s) => (
                        <Star key={s} className={`w-3 h-3 ${s <= r.rating ? "fill-amber-400" : "text-zinc-600"}`} />
                      ))}
                      <span className="ml-2 text-xs text-zinc-300 font-bold inline-flex items-center gap-1">
                        {r.buyer_name}<UserGem userId={r.buyer_id} size="xs" />
                      </span>
                    </div>
                    {r.text && <p className="text-sm text-zinc-200 mt-1.5">{r.text}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Right: sidebar */}
        <aside className="space-y-4">
          {/* Hours */}
          <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-hours-card">
            <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
              <Clock className="w-4 h-4" /> Hours
            </h2>
            <div className="mt-3">
              <HoursTable hours={shop.hours || {}} special_hours={shop.special_hours || []} />
            </div>
          </section>

          {/* Location */}
          {loc && (loc.address || loc.location_lat) && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-location-card">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Location
              </h2>
              {loc.address && <p className="mt-2 text-sm text-zinc-200">{loc.address}</p>}
              {loc.location_privacy && loc.location_privacy !== "public" && (
                <p className="text-[11px] text-cyan-300 mt-2" data-testid="storefront-location-privacy-note">
                  {loc.location_privacy === "approximate" && "Approximate area only — the exact spot is private."}
                  {loc.location_privacy === "exact_on_order" && "Exact address unlocks after a paid order."}
                  {loc.location_privacy === "pickup" && "Pickup location shown."}
                </p>
              )}
              {loc.location_lat && loc.location_lng && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${loc.location_lat},${loc.location_lng}`}
                  target="_blank" rel="noreferrer"
                  className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-pink-300 hover:text-pink-200"
                  data-testid="storefront-open-in-maps"
                >
                  Open in Maps <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {shop.delivery_radius_km && (
                <p className="mt-2 text-[11px] text-zinc-400">Delivery radius: {shop.delivery_radius_km} km</p>
              )}
            </section>
          )}

          {/* Verified credentials */}
          {(public_credentials.length > 0 || achievements.length > 0) && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-credentials-card">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-emerald-400" /> Trust & Verification
              </h2>
              <div className="mt-3 space-y-2">
                {public_credentials.map((c) => (
                  <div key={c.credential_id} className="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-3 flex items-center gap-2" data-testid={`storefront-credential-${c.credential_id}`}>
                    <CheckCircle2 className="w-4 h-4 text-emerald-300" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black">{CRED_LABEL[c.type] || c.type}{c.custom_type_label ? ` · ${c.custom_type_label}` : ""}</p>
                      <p className="text-[11px] text-emerald-200/80">
                        Hidden Gem Verified{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
                {achievements.map((a, i) => (
                  <div key={i} className="rounded-2xl bg-white/[0.02] border border-white/10 p-3 flex items-center gap-2 text-xs text-zinc-300" data-testid="storefront-achievement">
                    <Sparkles className="w-3.5 h-3.5 text-pink-300" />
                    <span className="font-bold">{CRED_LABEL[a.credential_type] || a.credential_type}</span>
                    <span className="text-zinc-500 ml-auto">awarded {new Date(a.awarded_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-zinc-500 mt-3">Only credential type + Hidden Gem Verified indicator is public. The underlying document is always kept private.</p>
            </section>
          )}

          {/* Team */}
          {team.length > 0 && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-team-card">
              <h2 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
                <Users className="w-4 h-4" /> The team
              </h2>
              <div className="mt-3 space-y-2">
                {team.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-3" data-testid={`storefront-team-${m.user_id}`}>
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 border border-white/10">
                      {m.avatar_url ? (
                        <img src={fileUrl(m.avatar_url)} alt={m.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-zinc-400 text-xs font-bold">{(m.name || "?").slice(0,1)}</div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate">{m.name || m.user_id}</p>
                      <p className="text-[11px] text-zinc-400 capitalize">{m.public_title || m.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Contact + social */}
          {(shop.phone || shop.website || shop.instagram || shop.tiktok) && (
            <section className="rounded-3xl bg-white/[0.03] border border-white/10 p-5" data-testid="storefront-contact-card">
              <h2 className="text-lg font-black tracking-tight">Get in touch</h2>
              <div className="mt-3 space-y-2 text-sm">
                {shop.phone && (
                  <a href={`tel:${shop.phone}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="storefront-contact-phone">
                    <Phone className="w-4 h-4 text-zinc-400" /> {shop.phone}
                  </a>
                )}
                {shop.website && (
                  <a target="_blank" rel="noreferrer" href={shop.website.startsWith("http") ? shop.website : `https://${shop.website}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="storefront-contact-website">
                    <Globe className="w-4 h-4 text-zinc-400" /> {shop.website}
                  </a>
                )}
                {shop.instagram && (
                  <a target="_blank" rel="noreferrer" href={`https://instagram.com/${shop.instagram.replace(/^@/, "")}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="storefront-contact-instagram">
                    <Instagram className="w-4 h-4 text-zinc-400" /> @{shop.instagram.replace(/^@/, "")}
                  </a>
                )}
                {shop.tiktok && (
                  <a target="_blank" rel="noreferrer" href={`https://tiktok.com/@${shop.tiktok.replace(/^@/, "")}`} className="flex items-center gap-2 text-zinc-200 hover:text-white" data-testid="storefront-contact-tiktok">
                    <Music2 className="w-4 h-4 text-zinc-400" /> @{shop.tiktok.replace(/^@/, "")}
                  </a>
                )}
              </div>
            </section>
          )}

          {/* Report */}
          {user && !isOwner && (
            <button
              onClick={() => navigate(`/support?report_shop=${shopId}`)}
              className="w-full text-center text-[11px] text-zinc-500 hover:text-zinc-300 py-2"
              data-testid="storefront-report"
            >
              Report this shop
            </button>
          )}
        </aside>
      </div>

      {/* QR modal */}
      {showQr && (
        <div className="fixed inset-0 z-[95] grid place-items-center bg-black/80 backdrop-blur-sm p-4" onClick={() => setShowQr(false)} data-testid="storefront-qr-modal">
          <div className="bg-white rounded-3xl p-6 flex flex-col items-center gap-3 max-w-xs" onClick={(e) => e.stopPropagation()}>
            <img src={`${backendBase}/api/shops/${shopId}/qr.png?size=512`} alt="Shop QR" className="w-64 h-64" data-testid="storefront-qr-image" />
            <p className="text-sm text-black font-bold text-center">Scan to open<br/><span className="text-zinc-600 text-xs font-normal">{shareUrl}</span></p>
            <a
              href={`${backendBase}/api/shops/${shopId}/qr.png?size=1024`}
              download={`${shopId}.png`}
              className="text-xs text-pink-500 hover:text-pink-600 font-black uppercase tracking-widest"
              data-testid="storefront-qr-download"
            >
              Download PNG
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
